export interface TimedPoint {
  latitude: number;
  longitude: number;
  dateTime: Date;
}

/**
 * The selected trips laid end to end, for playing them back.
 *
 * Within a trip the clock runs as the vehicle drove, so a stop at the lights takes as long in the
 * film as it did on the road. Between trips it does not: the hours a vehicle stood overnight are
 * cut, and the next trip starts the moment the last one ended.
 */
export interface PlaybackTimeline<P extends TimedPoint> {
  points: P[];
  /** Milliseconds into the film at which each point is reached. */
  offsets: number[];
  /** The index of the first point of each trip. */
  tripStarts: number[];
  duration: number;
}

export function buildPlaybackTimeline<P extends TimedPoint>(trips: P[][]): PlaybackTimeline<P> {
  const points: P[] = [];
  const offsets: number[] = [];
  const tripStarts: number[] = [];
  let elapsed = 0;

  for (const trip of trips) {
    if (trip.length === 0) continue;
    tripStarts.push(points.length);
    const tripStartMs = trip[0].dateTime.getTime();
    let last = 0;
    for (const point of trip) {
      // Never backwards, even if two readings arrive out of order: the film only runs one way.
      last = Math.max(last, point.dateTime.getTime() - tripStartMs);
      points.push(point);
      offsets.push(elapsed + last);
    }
    elapsed += last;
  }

  return { points, offsets, tripStarts, duration: elapsed };
}

export interface PlaybackPosition {
  /** The point last passed; the one whose details are shown. */
  index: number;
  latitude: number;
  longitude: number;
}

/** Where the film is at `time`, moving smoothly between two readings of the same trip. */
export function playbackPositionAt<P extends TimedPoint>(
  timeline: PlaybackTimeline<P>,
  time: number,
): PlaybackPosition {
  const { points, offsets } = timeline;
  if (points.length === 0) return { index: -1, latitude: 0, longitude: 0 };

  // The last point reached by `time`; where two share an offset (a trip boundary), the later one,
  // so the marker jumps to the next trip instead of drawing a line across to it.
  let low = 0;
  let high = offsets.length - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (offsets[middle] <= time) low = middle;
    else high = middle - 1;
  }

  const index = low;
  const point = points[index];
  const next = points[index + 1];
  const span = (offsets[index + 1] ?? 0) - offsets[index];
  if (!next || timeline.tripStarts.includes(index + 1) || span <= 0) {
    return { index, latitude: point.latitude, longitude: point.longitude };
  }

  const share = Math.min(1, Math.max(0, (time - offsets[index]) / span));
  return {
    index,
    latitude: point.latitude + (next.latitude - point.latitude) * share,
    longitude: point.longitude + (next.longitude - point.longitude) * share,
  };
}

/** What has been driven so far, one line per trip, ending at the marker. */
export function playedPaths<P extends TimedPoint>(
  timeline: PlaybackTimeline<P>,
  position: PlaybackPosition,
): [number, number][][] {
  if (position.index < 0) return [];

  const paths: [number, number][][] = [];
  timeline.tripStarts.forEach((start, trip) => {
    if (start > position.index) return;
    const end = Math.min(position.index, (timeline.tripStarts[trip + 1] ?? timeline.points.length) - 1);
    const path = timeline.points
      .slice(start, end + 1)
      .map(point => [point.latitude, point.longitude] as [number, number]);
    if (end === position.index) path.push([position.latitude, position.longitude]);
    paths.push(path);
  });
  return paths;
}

/** Compass bearing from one point to another, clockwise from north. */
function bearing(from: TimedPoint, to: TimedPoint): number {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const lat1 = toRadians(from.latitude);
  const lat2 = toRadians(to.latitude);
  const deltaLongitude = toRadians(to.longitude - from.longitude);
  const y = Math.sin(deltaLongitude) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLongitude);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/**
 * Which way the vehicle faces at point `index`: towards the next point it moves to within the same
 * trip, or at a trip's end the way it came. Null where the trip never moves.
 */
export function playbackHeading<P extends TimedPoint>(timeline: PlaybackTimeline<P>, index: number): number | null {
  const { points, tripStarts } = timeline;
  const point = points[index];
  if (!point) return null;
  const trip = tripStarts.filter(start => start <= index).length - 1;
  const tripStart = tripStarts[trip] ?? 0;
  const tripEnd = (tripStarts[trip + 1] ?? points.length) - 1;
  const moved = (other: P) => other.latitude !== point.latitude || other.longitude !== point.longitude;

  for (let next = index + 1; next <= tripEnd; next++) {
    if (moved(points[next])) return bearing(point, points[next]);
  }
  for (let previous = index - 1; previous >= tripStart; previous--) {
    if (moved(points[previous])) return bearing(points[previous], point);
  }
  return null;
}
